import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import type { DatabaseSourceInfo } from '@ditto/shared';
import { useEffect, useState, type FC } from 'react';
import { createRegexRenderer, RichTextarea } from 'rich-textarea';

// Query syntax highlighting renderer (colors from Figma design)
const queryRenderer = createRegexRenderer([
  [/\bAND\b/g, { color: '#ec7839', fontWeight: 500 }], // Orange for AND
  [/\b(OR|NOT)\b/g, { color: '#e261e5', fontWeight: 500 }], // Pink/Magenta for OR, NOT
  [/"[^"]*"/g, { color: '#3a93ff' }], // Blue for quoted strings
  [/\b\d{4}\b/g, { color: '#25c329' }], // Green for years/numbers
  [/[()[\]]/g, { color: '#737373' }], // Gray for parentheses and brackets
]);

type DateFilter = '' | '1year' | '5years' | '10years' | 'custom';
type TextAvailability = 'abstract' | 'free_full_text' | 'full_text';

interface PubmedInputsProps {
  initialQuery: string;
  onQueryChange: (query: string, isValid: boolean) => void;
  source: DatabaseSourceInfo;
  showErrors: boolean;
  setShowErrors: (showErrors: boolean) => void;
}

// Format date from YYYY-MM-DD to YYYY/M/D (PubMed format)
const formatDateToPubmed = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${year}/${parseInt(month, 10)}/${parseInt(day, 10)}`;
};

// Parse PubMed filter syntax from query
const parseFiltersFromQuery = (
  query: string
): {
  baseQuery: string;
  dateFilter: DateFilter;
  customStartDate: string;
  customEndDate: string;
  textAvailability: TextAvailability[];
} => {
  let baseQuery = query;
  let dateFilter: DateFilter = '';
  let customStartDate = '';
  let customEndDate = '';
  const textAvailability: TextAvailability[] = [];

  // Parse date filters
  if (baseQuery.includes('(y_1[Filter])')) {
    dateFilter = '1year';
    baseQuery = baseQuery.replace(/\s*AND\s*\(y_1\[Filter\]\)/gi, '');
  } else if (baseQuery.includes('(y_5[Filter])')) {
    dateFilter = '5years';
    baseQuery = baseQuery.replace(/\s*AND\s*\(y_5\[Filter\]\)/gi, '');
  } else if (baseQuery.includes('(y_10[Filter])')) {
    dateFilter = '10years';
    baseQuery = baseQuery.replace(/\s*AND\s*\(y_10\[Filter\]\)/gi, '');
  } else {
    // Check for custom date range: (YYYY/M/D:YYYY/M/D[pdat])
    const customDateMatch = baseQuery.match(
      /\s*AND\s*\((\d{4}\/\d{1,2}\/\d{1,2}):(\d{4}\/\d{1,2}\/\d{1,2})\[pdat\]\)/i
    );
    if (customDateMatch) {
      dateFilter = 'custom';
      // Convert YYYY/M/D to YYYY-MM-DD for date input
      const [, start, end] = customDateMatch;
      const [startYear, startMonth, startDay] = start.split('/');
      const [endYear, endMonth, endDay] = end.split('/');
      customStartDate = `${startYear}-${startMonth.padStart(2, '0')}-${startDay.padStart(2, '0')}`;
      // Only set end date if it's not the far future default
      if (end !== '3000/12/31') {
        customEndDate = `${endYear}-${endMonth.padStart(2, '0')}-${endDay.padStart(2, '0')}`;
      }
      baseQuery = baseQuery.replace(
        /\s*AND\s*\(\d{4}\/\d{1,2}\/\d{1,2}:\d{4}\/\d{1,2}\/\d{1,2}\[pdat\]\)/gi,
        ''
      );
    }
  }

  // Parse text availability filters
  if (baseQuery.includes('(fha[Filter])')) {
    textAvailability.push('abstract');
    baseQuery = baseQuery.replace(/\s*AND\s*\(fha\[Filter\]\)/gi, '');
  }
  if (baseQuery.includes('(ffrft[Filter])')) {
    textAvailability.push('free_full_text');
    baseQuery = baseQuery.replace(/\s*AND\s*\(ffrft\[Filter\]\)/gi, '');
  }
  if (baseQuery.includes('(fft[Filter])')) {
    textAvailability.push('full_text');
    baseQuery = baseQuery.replace(/\s*AND\s*\(fft\[Filter\]\)/gi, '');
  }

  return {
    baseQuery: baseQuery.trim(),
    dateFilter,
    customStartDate,
    customEndDate,
    textAvailability,
  };
};

export const PubmedInputs: FC<PubmedInputsProps> = ({
  initialQuery,
  onQueryChange,
  source,
  showErrors,
  setShowErrors,
}) => {
  const [query, setQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [textAvailability, setTextAvailability] = useState<TextAvailability[]>(
    []
  );

  // Parse initial query and extract filters
  useEffect(() => {
    const parsed = parseFiltersFromQuery(initialQuery);
    setQuery(parsed.baseQuery);
    setDateFilter(parsed.dateFilter);
    setCustomStartDate(parsed.customStartDate);
    setCustomEndDate(parsed.customEndDate);
    setTextAvailability(parsed.textAvailability);
  }, [initialQuery]);

  // Build final query with filters and notify parent
  useEffect(() => {
    const buildFinalQuery = (): string => {
      let finalQuery = query.trim();

      // Add date filter
      if (dateFilter === '1year') {
        finalQuery += ' AND (y_1[Filter])';
      } else if (dateFilter === '5years') {
        finalQuery += ' AND (y_5[Filter])';
      } else if (dateFilter === '10years') {
        finalQuery += ' AND (y_10[Filter])';
      } else if (dateFilter === 'custom' && customStartDate) {
        const start = formatDateToPubmed(customStartDate);
        const end = customEndDate
          ? formatDateToPubmed(customEndDate)
          : '3000/12/31';
        finalQuery += ` AND (${start}:${end}[pdat])`;
      }

      // Add text availability filters
      if (textAvailability.includes('abstract')) {
        finalQuery += ' AND (fha[Filter])';
      }
      if (textAvailability.includes('free_full_text')) {
        finalQuery += ' AND (ffrft[Filter])';
      }
      if (textAvailability.includes('full_text')) {
        finalQuery += ' AND (fft[Filter])';
      }

      return finalQuery;
    };

    const finalQuery = buildFinalQuery();
    const isValid = query.trim().length > 0;
    if (isValid) {
      setShowErrors(false);
    }
    onQueryChange(finalQuery, isValid);
  }, [
    query,
    dateFilter,
    customStartDate,
    customEndDate,
    textAvailability,
    onQueryChange,
    setShowErrors,
  ]);

  const handleTextAvailabilityChange = (
    filter: TextAvailability,
    checked: boolean
  ) => {
    if (checked) {
      setTextAvailability(prev => [...prev, filter]);
    } else {
      setTextAvailability(prev => prev.filter(f => f !== filter));
    }
  };

  return (
    <>
      {/* Search Query */}
      <div className="flex flex-col gap-1 w-full">
        <Text variant="body-sm" className="font-medium">
          <span className="text-red-500">*</span>Database query
        </Text>
        <RichTextarea
          placeholder={source.queryPlaceholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          className={cn(
            'rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            showErrors && 'border-red-500'
          )}
          style={{ width: '100%', minHeight: 100 }}
        >
          {queryRenderer}
        </RichTextarea>
        {showErrors && (
          <Text variant="body-xs" color="danger">
            A query is required to add database items as rows in the table
          </Text>
        )}
      </div>

      {/* Publication Date Filter */}
      <div className="flex flex-col gap-2 w-full mt-4">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Publication Date
        </Text>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {[
            { value: '1year', label: '1 year' },
            { value: '5years', label: '5 years' },
            { value: '10years', label: '10 years' },
            { value: 'custom', label: 'Custom' },
          ].map(option => (
            <label
              key={option.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="radio"
                name="dateFilter"
                value={option.value}
                checked={dateFilter === option.value}
                onChange={e => setDateFilter(e.target.value as DateFilter)}
                className="h-4 w-4 text-primary border-input focus:ring-primary"
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
          {dateFilter && (
            <button
              type="button"
              onClick={() => {
                setDateFilter('');
                setCustomStartDate('');
                setCustomEndDate('');
              }}
              className="flex items-center text-sm text-primary hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* Custom Date Range */}
        {dateFilter === 'custom' && (
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div className="flex flex-col gap-1">
              <Label className="text-neutral-600">Start Date</Label>
              <input
                type="date"
                value={customStartDate}
                onChange={e => setCustomStartDate(e.target.value)}
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-neutral-600">End Date (optional)</Label>
              <input
                type="date"
                value={customEndDate}
                onChange={e => setCustomEndDate(e.target.value)}
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>
        )}
      </div>

      {/* Text Availability Filter */}
      <div className="flex flex-col gap-2 w-full mb-4">
        <Text variant="body-sm" className="font-medium text-neutral-600">
          Text Availability
        </Text>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {[
            { value: 'abstract', label: 'Abstract' },
            { value: 'free_full_text', label: 'Free full text' },
            { value: 'full_text', label: 'Full text' },
          ].map(option => (
            <label
              key={option.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Checkbox
                checked={textAvailability.includes(
                  option.value as TextAvailability
                )}
                onCheckedChange={checked =>
                  handleTextAvailabilityChange(
                    option.value as TextAvailability,
                    checked === true
                  )
                }
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  );
};
